/**
 * Resources Context
 * Centralized resource/translation management system
 * Provides API-based resource loading with caching and fallback support
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type ResourcesContextValue = {
    getResourceValue: (key: string, fallback?: string) => string;
    isLoading: boolean;
};

const ResourcesContext = createContext<ResourcesContextValue | undefined>(undefined);

type ResourcesProviderProps = {
    children: ReactNode;
};

// Use environment variable for API URL
const RESOURCES_API = import.meta.env.VITE_RESOURCES_API_URL || '';

export function ResourcesProvider({ children }: ResourcesProviderProps) {
    const [resources, setResources] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        
        async function loadResources() {
            try {
                const lang = import.meta.env.VITE_DEFAULT_LANGUAGE || 'en'; // Get from environment or default to 'en'
                
                // Step 1: Fetch signed URL from your API
                const signedUrlRes = await fetch(
                    `${RESOURCES_API}?lang=${encodeURIComponent(lang)}&platform=web&appName=qms`,
                    { cache: "no-store" }
                );
                
                if (!signedUrlRes.ok) {
                    throw new Error(`Signed URL fetch failed: ${signedUrlRes.status}`);
                }
                
                const signedUrl = (await signedUrlRes.text()).trim();
                if (!signedUrl) throw new Error("Empty signed URL");

                // Step 2: Fetch resources JSON using signed URL
                const response = await fetch(signedUrl, { cache: "no-store" });
                
                if (!response.ok) {
                    throw new Error(`Resource fetch failed: ${response.status}`);
                }
                
                const json = await response.json();
                
                if (isMounted && json && typeof json === "object") {
                    setResources(json as Record<string, string>);
                    setIsLoading(false);
                } else if (isMounted) {
                    setIsLoading(false);
                }
            } catch (err) {
                console.error("Error loading resources:", err);
                if (isMounted) setIsLoading(false);
            }
        }
        
        loadResources();
        
        return () => { 
            isMounted = false; 
        };
    }, []);

    const value = useMemo<ResourcesContextValue>(() => ({
        // CRITICAL: Support fallback parameter, use it if provided, otherwise use key as fallback
        getResourceValue: (key: string, fallback?: string) => {
            return resources[key] ?? fallback ?? key;
        },
        isLoading,
    }), [resources, isLoading]);

    return (
        <ResourcesContext.Provider value={value}>
            {children}
        </ResourcesContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useResourcesContext(): ResourcesContextValue {
    const ctx = useContext(ResourcesContext);
    
    // Provide safe fallback if context is not available
    if (!ctx) {
        return { 
            getResourceValue: (key: string, fallback?: string) => fallback ?? key, 
            isLoading: false 
        };
    }
    
    return ctx;
}

